'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
  image: string;
  alt: string;
  slug: string;
}

interface BlogPostContentProps {
  slug: string;
}

const BlogPostContent = ({ slug }: BlogPostContentProps) => {
  const router = useRouter();
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Mock blog posts data (in real app, this would come from CMS or API)
  const blogPosts: BlogPost[] = [
  {
    id: '2',
    title: '🌿 Posidonia oceanica in Cyprus. Protected Seagrass Meadows and Marine Life Guide for Divers',
    excerpt: 'If you have ever dived in Cyprus, you have seen it. Stretching across sandy bays and gently sloping seabeds, forming dense green underwater meadows. Posidonia oceanica is one of the most important marine habitats in the Mediterranean.',
    content: `If you have ever dived in Cyprus, you have seen it.

Stretching across sandy bays and gently sloping seabeds, forming dense green underwater meadows. Posidonia oceanica is one of the most important marine habitats in the Mediterranean.

Yet many divers mistake it for simple seaweed.

It is not seaweed.

It is a protected marine flowering plant that supports marine life, improves water clarity and plays a vital role in Cyprus' coastal ecosystem.

This guide explains:

- what Posidonia oceanica is
- what it looks like underwater
- what marine life lives there
- why it is legally protected in Europe and Cyprus
- and how divers can help preserve it

## 🌊 What is Posidonia oceanica?

Posidonia oceanica is a marine flowering plant endemic to the Mediterranean Sea.

Unlike algae, it:

- has roots
- produces flowers and seeds
- forms structured underwater meadows
- grows extremely slowly

It typically grows from shallow coastal waters down to around 30–40 metres, depending on light conditions.

In Cyprus, Posidonia meadows are commonly found in:

- sandy bays
- shallow shore dive sites
- artificial reef zones
- transitional areas between reef and sand

Many divers pass over these meadows on the way to reef or wreck dives — without realising they are hovering above one of the Mediterranean's most important ecosystems.

## 🌿 What does Posidonia look like underwater?

To divers, Posidonia meadows resemble thick green grass swaying in the current.

Look closer, and you'll see:

- long ribbon-like leaves, often 30–100 cm in length
- dense root systems anchoring into sediment
- layered, raised "mattes" formed over decades or centuries
- sandy corridors between clusters of growth

In clear Cypriot water, these meadows move rhythmically with surge and current, creating a living underwater landscape.

In some areas, older Posidonia beds slightly elevate the seabed, stabilising sand and reducing erosion.

It may look simple, but it is ecological infrastructure.

## 🐟 What marine life lives in Posidonia meadows in Cyprus?

Posidonia oceanica meadows are biodiversity hotspots.

For divers in Cyprus, these habitats are home to:

### 🐠 Juvenile fish

- Damselfish
- Seabream
- Wrasse species
- Small groupers
- Pipefish hiding among blades

These meadows act as nursery grounds where young fish grow before moving to reefs and wrecks such as the Zenobia.

### 🐙 Invertebrates

- Octopus concealed in sandy patches
- Sea cucumbers
- Crabs and small crustaceans
- Nudibranchs in transitional zones

### 🐢 Sea turtles

Green and loggerhead turtles are often seen feeding in or near seagrass beds in Cyprus' shallow coastal areas.

### 🐟 Larger predators

Species such as dusky groupers and amberjack may patrol the meadow edges where sand meets reef.

Many of the species divers enjoy on Cyprus wreck dives begin life hidden within these seagrass ecosystems.

Healthy Posidonia meadows directly support fish abundance across the island.

## 💧 Why Posidonia matters for diving conditions in Cyprus

For divers, visibility and stability matter.

Posidonia oceanica helps:

- stabilise the seabed
- trap sediment
- reduce coastal erosion
- improve water clarity
- absorb nutrients
- store significant amounts of carbon ("blue carbon")

Clear Mediterranean water and thriving marine life are not accidental.

They are supported by ecosystems like Posidonia.

## ⚖️ Legal protection of Posidonia oceanica in Europe

Posidonia oceanica is strictly protected under European law.

It is recognised as a Priority Habitat (Habitat 1120) under the EU Habitats Directive (92/43/EEC).

This requires:

- designation of Special Areas of Conservation (SACs)
- prevention of habitat deterioration
- active protection measures

It is also protected under:

- The Bern Convention
- The Barcelona Convention on Specially Protected Areas and Biodiversity
- The Marine Strategy Framework Directive (MSFD), where Posidonia is used as an ecological indicator
- The Common Fisheries Policy (CFP), which prohibits destructive towed fishing gear such as trawls and dredges above Posidonia beds

At a European policy level, Posidonia is considered one of the Mediterranean's most valuable marine habitats.

## 🇨🇾 Protection of Posidonia in Cyprus

Cyprus implements these protections through national legislation and marine management.

### 🌿 Natura 2000 Marine Sites

Several marine areas in Cyprus are designated under the Natura 2000 network, including:

- Cavo Greco
- Nissia
- Akamas Peninsula

These include important Posidonia meadows and are subject to conservation measures.

### 🐟 Fisheries restrictions

The Department of Fisheries and Marine Research enforces protections including:

- prohibition of trawling in waters shallower than 55 metres
- seabed protection measures

### 🏛️ Sensitive cultural sites

Protection also extends to vulnerable areas such as near the Amathounta archaeological site, where ecological and cultural heritage intersect.

## 🌱 Restoration efforts in Cyprus

Protection alone is not always enough.

Active research and restoration initiatives are underway in Cyprus.

One important project is the RESPOSCY initiative (REStoring POSidonia oceanica Meadows of CYprus), which focuses on monitoring, mapping and restoring damaged meadows.

You can learn more about this project here:

👉 https://merresearch.com/portfolio-item/restoring-posidonia-oceanica-meadows-of-cyprus-resposcy-2025-ongoing/

Supporting awareness of such initiatives helps ensure that Cyprus' marine ecosystems remain healthy for future divers.

## ⚠️ Why Posidonia remains vulnerable

Despite legal protection, Posidonia meadows are still threatened by:

- careless anchoring
- coastal development
- sediment disturbance
- pollution
- irresponsible boating

Damage can take decades to recover due to the plant's slow growth rate.

## 🤿 What divers in Cyprus can do

As divers, we can actively contribute to protecting Posidonia meadows by:

- maintaining proper buoyancy
- avoiding contact with the seabed
- minimising fin kicks in shallow sandy areas
- respecting no-anchor zones
- choosing responsible dive operators

Good buoyancy control is conservation in action.

## ❓ FAQ – Posidonia oceanica and Diving in Cyprus

**Is Posidonia oceanica seaweed?**
No. It is a marine flowering plant, not algae.

**Why is Posidonia important for divers in Cyprus?**
It supports marine life, improves visibility and stabilises coastal ecosystems.

**Is Posidonia protected in Cyprus?**
Yes. It is protected under European legislation and national conservation measures.

**Can divers damage Posidonia?**
Yes. Poor buoyancy, fin contact and sediment disturbance can cause long-term harm.

## 🌿 Final takeaway

Posidonia oceanica may appear simple.

But it is one of the foundations of Cyprus' underwater ecosystem.

The fish life, visibility and healthy marine environments divers enjoy across the island depend heavily on these seagrass meadows.

Next time you descend over a sandy bay in Cyprus, look closer.

You are not just hovering above grass.

You are above one of the Mediterranean's most important marine habitats.

## ✍️ Author

**Demetra Regan**
Cyprus-based scuba diving instructor and founder of CyprusDiving.info. Providing independent, locally grounded information on diving in Cyprus, marine life and responsible diving practices.`,
    category: 'conservation',
    author: 'Demetra Regan',
    date: '2026-02-18',
    readTime: '9 min read',
    image: "/assets/images/shutterstock_2541104003-1771398118881.jpg",
    alt: 'Posidonia oceanica seagrass meadow Mediterranean ecosystem Cyprus underwater',
    slug: 'posidonia-oceanica-cyprus-protected-seagrass'
  },
  {
    id: '1',
    title: '🇨🇾 Tipping in Cyprus for Divers – Should You Tip at Dive Centres?',
    excerpt: 'If you are planning a diving holiday in Cyprus, one question often comes up during trip planning: Should you tip at dive centres in Cyprus – and if so, how much? This guide explains clearly and honestly what is expected, how Cyprus differs from major dive destinations, and how you can show appreciation in a way that fits local culture.',
    content: `🇨🇾 Tipping in Cyprus for Divers – Should You Tip at Dive Centres?

Demetra Regan 6 February 2026 · 8 min read

If you are planning a diving holiday in Cyprus, one question often comes up during trip planning:

Should you tip at dive centres in Cyprus – and if so, how much?

Tipping customs vary widely around the world, and many divers arriving from the UK, Europe, the US or the Middle East are unsure what is considered normal, expected or appropriate when diving in Cyprus.

This guide explains clearly and honestly:

- whether tipping is expected at dive centres in Cyprus
- how tipping works for instructors, guides and boat crew
- how Cyprus differs from major dive destinations such as Egypt, Thailand or Mexico
- and how you can show appreciation in a way that fits local culture.

This article is written from the perspective of a Cyprus-based scuba diving instructor actively working with local dive centres and reflects how the local dive industry operates today.

## 💡 Short answer: Is tipping expected when diving in Cyprus?

No. Tipping is not expected at dive centres in Cyprus.

Unlike some popular international dive destinations, Cyprus does not operate on a tipping-based service culture.

Dive professionals in Cyprus are paid wages by their employers and do not rely on tips as part of their income.

If you choose to tip, it is seen as a kind gesture — not a requirement.

## Why tipping culture in Cyprus is different

Cyprus follows the general Southern European and Mediterranean service culture.

In practice this means:

- service staff are paid by their employer
- tips are not built into salary expectations
- good service is considered part of the professional standard

This applies not only to dive centres, but also to:

- restaurants
- taxis
- hotels
- tour operators

For divers, this means you should never feel uncomfortable, pressured or embarrassed if you do not leave a tip.

## 🧭 Do divers tip instructors and guides in Cyprus?

Most divers in Cyprus do not tip their instructor or guide as a standard practice.

However, some divers choose to tip when:

- an instructor provided exceptional personal support
- a guide went out of their way to assist nervous or inexperienced divers
- a private guiding service was provided
- a course involved many additional hours of personal attention

If you decide to tip, it is always treated as a personal thank-you rather than a professional obligation.

## 🚤 What about boat crew and skippers?

The same rule applies to boat diving.

Boat crew and skippers in Cyprus are paid staff. Tipping is not expected and is not normally requested.

If you receive outstanding service and would like to show appreciation, a small voluntary tip is welcomed — but never required.

Note: Tipping practices may differ on international liveaboards or foreign-operated boats visiting Cyprus.

## 💶 How much should you tip if you choose to?

There is no official or informal scale for tipping in Cyprus diving.

If you feel you would genuinely like to leave something, a simple guideline is:

- €5–€10 per diver for a day of diving
- or a small group tip after a multi-day course or guided programme

Anything above this is entirely at your discretion.

There is no percentage-based expectation.

## 🛑 Important: You should never be asked for a tip

In Cyprus, it would be considered unprofessional for a dive centre, instructor or guide to:

- request tips
- suggest tipping
- or hint that tips are expected

If you ever encounter pressure to tip, this would be unusual for the local diving industry.

## 🤝 Better ways to support your dive centre

Many local dive centres value other forms of support far more than tips.

Consider:

- leaving a positive Google review for your local dive centre (see our Dive Centres Directory)
- tagging the dive centre on social media
- recommending them to other divers
- returning for future dives
- or booking additional local diving experiences

For small independent operators, positive visibility is often more valuable than cash tips.

## 🇨🇾 How Cyprus compares with other diving destinations

Divers often arrive in Cyprus with habits formed in other regions.

| Destination | Is tipping expected when diving? |
|-------------|----------------------------------|
| Egypt (Red Sea) | Yes – commonly expected |
| Thailand | Common in tourist dive centres |
| Mexico | Common in tourist dive centres |
| Malta | Similar to Cyprus – not expected |
| Greece | Similar to Cyprus – not expected |
| Cyprus | ❌ Not expected |

Cyprus follows a European professional service model rather than a tipping-based tourism model.

## 🐙 Local insider tip for divers in Cyprus

If you want to show appreciation in a very Cypriot way:

Invite your instructor or guide for a coffee or soft drink after the dive — especially at small local centres.

This reflects the social culture of small local dive communities in Cyprus more than a cash tip.

## ❓ Quick FAQ

**Do I have to tip dive instructors in Cyprus?**
No. Tipping is not expected in Cyprus dive centres. Any tip is entirely voluntary.

**Is tipping expected on boat dives in Cyprus?**
No. Boat crew and skippers are paid staff and do not rely on tips.

**Is it rude not to tip in Cyprus?**
No. It is culturally normal not to tip for professional services, including diving.

## Final takeaway

Tipping in Cyprus dive centres is optional, not expected and never required.

If you enjoyed your dives, the best support you can give is:

- respect the local environment
- follow responsible diving practices
- and support independent local operators through reviews and recommendations.

## ✍️ Author

**Demetra Regan**
Cyprus-based scuba diving instructor and founder of CyprusDiving.info. Demetra provides independent, locally verified information on diving in Cyprus, including dive sites, marine life, safety regulations and responsible diving practices.`,
    category: 'community',
    author: 'Demetra Regan',
    date: '2026-02-06',
    readTime: '8 min read',
    image: "/assets/images/image-1770729113628.png",
    alt: 'Illustrated octopus with diving gear and tipping jar representing Cyprus dive centre tipping guide',
    slug: 'tipping-in-cyprus-for-divers'
  }
];


  const post = blogPosts.find((p) => p.slug === slug);

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="animate-pulse bg-muted h-96 rounded-xl"></div>
            <div className="animate-pulse bg-muted h-12 rounded-lg"></div>
            <div className="animate-pulse bg-muted h-64 rounded-lg"></div>
          </div>
        </div>
      </div>);

  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Icon name="ExclamationTriangleIcon" size={64} className="text-muted-foreground mx-auto" />
          <h1 className="text-3xl font-bold text-foreground">Article Not Found</h1>
          <p className="text-muted-foreground">The article you're looking for doesn't exist.</p>
          <button
            onClick={() => router.push('/blog')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all duration-200 hover:opacity-90"
            style={{ background: '#6FD3E0', color: '#0B1622', border: '1.5px solid #6FD3E0' }}
          >
            <Icon name="ArrowLeftIcon" size={20} />
            <span>Back to Blog</span>
          </button>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <article className="container mx-auto px-4 py-12 lg:py-16">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => router.push('/blog')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all duration-200 hover:opacity-90"
            style={{ background: '#6FD3E0', color: '#0B1622', border: '1.5px solid #6FD3E0' }}
          >
            <Icon name="ArrowLeftIcon" size={20} />
            <span className="font-medium">Back to Blog</span>
          </button>

          {/* Article Header */}
          <header className="space-y-6 mb-12">
            <div className="inline-flex items-center space-x-2 bg-secondary/10 text-secondary px-4 py-2 rounded-full text-sm font-medium">
              <Icon name="BookOpenIcon" size={16} />
              <span className="capitalize">{post.category.replace('-', ' ')}</span>
            </div>

            <h1 className="text-4xl lg:text-5xl font-headline font-bold text-foreground leading-tight">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Icon name="UserCircleIcon" size={20} />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CalendarIcon" size={20} />
                <span>{new Date(post.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="ClockIcon" size={20} />
                <span>{post.readTime}</span>
              </div>
            </div>
          </header>

          {/* Featured Image */}
          <div className="relative w-full h-[400px] lg:h-[500px] rounded-2xl overflow-hidden mb-12">
            <AppImage
              src={post.image}
              alt={post.alt}
              className="object-cover w-full h-full" />
            
          </div>

          {/* Article Content with Markdown Rendering */}
          <div className="prose prose-lg prose-slate max-w-none
            prose-headings:font-headline prose-headings:font-bold prose-headings:text-foreground
            prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
            prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
            prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:mb-6
            prose-a:text-primary prose-a:no-underline hover:prose-a:underline
            prose-strong:text-foreground prose-strong:font-semibold
            prose-ul:my-6 prose-ul:space-y-2
            prose-ol:my-6 prose-ol:space-y-2
            prose-li:text-muted-foreground
            prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:pl-6 prose-blockquote:italic
            prose-code:text-secondary prose-code:bg-secondary/10 prose-code:px-2 prose-code:py-1 prose-code:rounded
            prose-table:w-full prose-table:border-collapse
            prose-th:bg-primary/10 prose-th:border prose-th:border-border prose-th:p-3 prose-th:text-left prose-th:font-semibold
            prose-td:border prose-td:border-border prose-td:p-3
            prose-hr:my-12 prose-hr:border-border
          ">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {post.content}
            </ReactMarkdown>
          </div>

          {/* Share Section */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-2 text-muted-foreground">
                <Icon name="ShareIcon" size={20} />
                <span className="font-medium">Share this article</span>
              </div>
              <div className="flex items-center space-x-3">
                {/* Facebook Share */}
                <button 
                  onClick={() => {
                    const url = encodeURIComponent(window.location.href);
                    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=600,height=400');
                  }}
                  className="p-2 bg-card hover:bg-[#1877F2] hover:text-white rounded-lg transition-colors duration-300 group"
                  aria-label="Share on Facebook"
                >
                  <svg className="w-5 h-5 text-foreground group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </button>

                {/* Twitter/X Share */}
                <button 
                  onClick={() => {
                    const url = encodeURIComponent(window.location.href);
                    const text = encodeURIComponent(post.title);
                    window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank', 'width=600,height=400');
                  }}
                  className="p-2 bg-card hover:bg-[#000000] hover:text-white rounded-lg transition-colors duration-300 group"
                  aria-label="Share on Twitter"
                >
                  <svg className="w-5 h-5 text-foreground group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </button>

                {/* LinkedIn Share */}
                <button 
                  onClick={() => {
                    const url = encodeURIComponent(window.location.href);
                    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank', 'width=600,height=400');
                  }}
                  className="p-2 bg-card hover:bg-[#0A66C2] hover:text-white rounded-lg transition-colors duration-300 group"
                  aria-label="Share on LinkedIn"
                >
                  <svg className="w-5 h-5 text-foreground group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </button>

                {/* Copy Link */}
                <button 
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    // Optional: Add a toast notification here
                  }}
                  className="p-2 bg-card hover:bg-muted rounded-lg transition-colors duration-300"
                  aria-label="Copy link"
                >
                  <Icon name="LinkIcon" size={20} className="text-foreground" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Related Articles CTA */}
      <div className="bg-gradient-to-b from-background to-primary/5 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-3xl font-headline font-bold text-foreground">
              Explore More Cyprus Diving Content
            </h2>
            <p className="text-lg text-muted-foreground">
              Discover dive sites, marine life, and local diving insights
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                href="/blog"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all duration-200 hover:opacity-90"
                style={{ background: '#6FD3E0', color: '#0B1622', border: '1.5px solid #6FD3E0' }}
              >
                <Icon name="NewspaperIcon" size={20} />
                <span>More Articles</span>
              </Link>
              <Link
                href="/regional-dive-sites"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all duration-200 hover:bg-cyan-400/10"
                style={{ background: 'transparent', color: '#6FD3E0', border: '1.5px solid #6FD3E0' }}
              >
                <Icon name="MapPinIcon" size={20} />
                <span>Dive Sites</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default BlogPostContent;